import React, { useEffect, useState } from "react";
import { Box, Flex, Text, Icon, VStack, Spinner, Link } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import ChatIcon from "@root/assets/icons/chaticon";
import ToggleButtonGroup from "@root/components/togglebutton";
import { useDashboard } from "@root/hooks/useDashboard";
import { useNavigate, useSearchParams } from "react-router-dom";
import LeftArrowIcon from "@root/assets/icons/leftarrowicon";
import { TopHeader } from "@root/components/DashboardTopHeader/topheader";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { CHATBOT_TYPE, LAW_TYPES, PAGINATION_DEFAULTS } from "@root/constants";
import { useSelector } from "react-redux";
import { Paginate } from "@root/components/pagination";
import { getChatRoutebythreadid } from "@root/utils";

export const ChatHistoryPage: React.FC = () => {
  const { t } = useTranslation("common");
  const [selectedToggle, setSelectedToggle] = useState<string>("law");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const title = searchParams.get("title");
  const chatbottype = searchParams.get("type");

  const {
    eachChatbotChatsLoading,
    eachChatbotChats,
    eachchatbotChatsCount,
    fetchEachChatbotChat,
  }: {
    fetchEachChatbotChat: any;
    eachChatbotChats: any;
    eachChatbotChatsLoading: any;
    eachchatbotChatsCount: any;
  } = useDashboard();

  console.log("eachChatbotChats", eachChatbotChats);

  const isLoggedInUser: any = useSelector(getLoggedInUser);

  useEffect(() => {
    const payload: any = {
      type: chatbottype,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      page: pageNo,
    };

    if (chatbottype === CHATBOT_TYPE.LAW_CHATBOT) {
      payload.source =
        selectedToggle === LAW_TYPES.LAW
          ? LAW_TYPES.LAW
          : selectedToggle === LAW_TYPES.LAW_CASE
          ? "law_case"
          : undefined;
    }

    fetchEachChatbotChat(payload);
  }, [selectedToggle, pageNo]);

  const renderChats = (chats: any[]) => {
    return chats?.map((chat: any) => (
      <Flex
        key={chat?.id}
        p={3}
        border="1px solid"
        borderColor="gray.200"
        rounded="md"
        align="center"
        bg="white"
        shadow="sm"
        cursor={"pointer"}
        _hover={{ bg: "#518B8A33" }}
        onClick={() => navigate(getChatRoutebythreadid(chat))}
      >
        <Icon as={ChatIcon} boxSize={8} mr={2} color="gray.500" />
        <Text fontSize="sm">{chat?.question}</Text>
      </Flex>
    ));
  };

  const formatName = (name: string) =>
    name?.charAt(0)?.toUpperCase() + name?.slice(1);

  function handlePageChange(newPage: number) {
    setPageNo(newPage);
    fetchEachChatbotChat({
      type: chatbottype,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      page: newPage,
    });
  }

  return (
    <Box height="100%" display="flex" flexDirection="column" w="100%">
      {/* Fixed Header */}
      <Box flexShrink={0} p={6} pt={0} pb={4} ml={"30px"}>
        <Text
          display="inline-flex"
          alignItems="center"
          gap={2}
          color="#40599B"
          py={5}
          cursor="pointer"
          onClick={() => navigate("/dashboard")}
        >
          <LeftArrowIcon />
          {t("dashboard.goBack")}
        </Text>

        {/* Header with breadcrumb + toggle */}
        <Flex flexDirection="row" justify="space-between" align="center" mb={4}>
          <Text fontSize="sm" color="gray.500">
            <Link href="/dashboard">{t("sidebar.dashboard")}</Link> /{" "}
            <Text as="span" fontWeight="bold" color="#252525" fontSize="14px">
              {title}
            </Text>
          </Text>

          {chatbottype === CHATBOT_TYPE.LAW_CHATBOT && (
            <ToggleButtonGroup
              options={[
                { label: t("translate.law"), value: "law" },
                { label: t("translate.lawCase"), value: "lawCase" },
              ]}
              defaultSelected="law"
              onChange={(value) => setSelectedToggle(value)}
            />
          )}
        </Flex>
      </Box>

      {/* Scrollable Content Area */}
      <Box flex="1" overflowY="auto" minHeight="0" px={6}>
        {eachChatbotChatsLoading ? (
          <Flex justify="center" align="center" height="100%" fontSize="16px">
            <Spinner size="lg" />
          </Flex>
        ) : (
          <VStack
            align="stretch"
            spacing={3}
            w="100%"
            maxW="900px"
            mx="auto"
            py={4}
          >
            {(() => {
              let chats = eachChatbotChats;

              if (!chats || chats.length === 0) {
                return (
                  <Box
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text color="gray.500" textAlign="center">
                      {t("chatHistory.noHistoryFound")}
                    </Text>
                  </Box>
                );
              }

              return renderChats(chats);
            })()}
          </VStack>
        )}
      </Box>

      {/* Fixed Pagination */}
      <Box flexShrink={0} mt={2} pb={5} bg="white" shadow="md">
        <Flex justifyContent="center">
          <Paginate
            page={pageNo}
            count={eachchatbotChatsCount}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            onPageChange={handlePageChange}
          />
        </Flex>
      </Box>
    </Box>
  );
};
